// Trim the token defensively. The secret was stored in Vercel with a trailing
// newline, which got URL-encoded as %0A onto every Mapbox request and returned
// 401 (Unauthorized) on all driving-traffic / alternate-route calls. Trimming
// here fixes it regardless of how the env var is stored, so it can't recur.
const RAW_MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
export const MAPBOX_TOKEN = RAW_MAPBOX_TOKEN?.trim() || undefined;
// Google Places key lives server-side in the `places-along-route` edge function.

export type EnvCheck = { ok: boolean; missing: string[] };

export function checkRequiredEnv(): EnvCheck {
  const missing: string[] = [];
  if (!MAPBOX_TOKEN) missing.push("VITE_MAPBOX_TOKEN");
  return { ok: missing.length === 0, missing };
}

// build: force fresh Vercel build to bake in VITE_MAPBOX_TOKEN env var

// ---------------------------------------------------------------------------
// APP VERSION — stamped onto contributions (route_feedback.app_version) so the
// dashboard can tell which build a rating came from (Morgan, Aug 7: "i want to
// know which version they are using").
//
// This was `export const APP_VERSION = "2.3"` with a comment asking a human to
// keep it in sync with Xcode. On 2.4 the human did not, so every rating from
// the shipped build was filed under the previous version. There is now no
// literal to forget: see src/lib/versionSource.ts.
//
// TWO SOURCES, IN ORDER OF AUTHORITY:
//   1. what the OS reports for the INSTALLED bundle (native only, async);
//   2. MARKETING_VERSION baked in at build time from project.pbxproj.
// (2) is right unless someone bumps the version in Xcode and archives without
// rebuilding the web bundle -- which is precisely how a stale stamp ships, so
// (1) exists to catch exactly that and says so in the console when it does.
// ---------------------------------------------------------------------------

declare const __APP_VERSION__: string;
declare const __APP_BUILD__: string;

/** Baked in by vite.config.ts. Guarded because vitest does not define it. */
const BUILT_VERSION = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "0.0.0-dev";
const BUILT_BUILD = typeof __APP_BUILD__ === "string" ? __APP_BUILD__ : "0";

let nativeVersion: string | null = null;
let nativeBuild: string | null = null;

/** The version to stamp on anything a user contributes. Call it — do not
 *  cache it at module load, or you get the build stamp forever and the native
 *  correction below never lands. */
export function appVersion(): string {
  return nativeVersion ?? BUILT_VERSION;
}

/** Marketing version plus build number, e.g. "2.4 (48)". The build number is
 *  what separates two TestFlight builds of the same version, which the
 *  marketing version alone cannot do. Diagnostics and logs, not the database. */
export function appVersionFull(): string {
  return `${appVersion()} (${nativeBuild ?? BUILT_BUILD})`;
}

/** Ask the OS what it actually installed. Call once at startup; a no-op on web
 *  and anywhere the plugin is unavailable, where the build stamp stands. */
export async function initAppVersion(): Promise<void> {
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    if (info?.version) nativeVersion = String(info.version);
    if (info?.build) nativeBuild = String(info.build);
    if (nativeVersion && nativeVersion !== BUILT_VERSION) {
      // NOT silent (rule 17). This means the JS in the shipped app was built
      // before the version bump — the bundle and the binary disagree, and
      // every stamp written before this line resolved used the stale one.
      console.warn(
        `[version] STALE WEB BUNDLE — the installed app is ${nativeVersion} ` +
        `(build ${nativeBuild ?? "?"}) but its JS was built for ${BUILT_VERSION}. ` +
        `Run the web build and \`npx cap sync ios\` before archiving.`,
      );
    }
  } catch {
    /* web, or the plugin is unavailable — the build stamp is the answer */
  }
}
